// src/api/vacantesService.js
import api from './api';

const vacantesService = {
  // ===============================
  // VACANTES
  // ===============================

  async getVacantesActivas(token) {
    try {
      const response = await api.get('/vacantes', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener vacantes:', error.response?.data || error.message);
      throw error;
    }
  },

  async crearVacante(vacante, token) {
    try {
      const response = await api.post('/vacantes', vacante, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear vacante:', error.response?.data || error.message);
      throw error;
    }
  },

  async crearVacanteDirecta(vacante, token) {
    return this.crearVacante(vacante, token);
  },

  async buscarVacantes(filtros) {
    try {
      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== '' && filtros[key] !== null && filtros[key] !== undefined) {
          params.append(key, filtros[key]);
        }
      });
      const response = await api.get(`/vacantes/buscar?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al buscar vacantes:', error.response?.data || error.message);
      throw error;
    }
  },

  async getVacantePorId(id, token) {
    try {
      const response = await api.get(`/vacantes/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener vacante por ID:', error.response?.data || error.message);
      throw error;
    }
  },

  // Alias para compatibilidad
  async getVacanteById(id, token) {
    return this.getVacantePorId(id, token);
  },

  async actualizarVacante(id, datos, token) {
    try {
      const response = await api.put(`/vacantes/${id}`, datos, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar vacante:', error.response?.data || error.message);
      throw error;
    }
  },

  async cerrarVacante(vacanteId, motivoCierre, cerradoPor, token) {
    try {
      const response = await api.put(
        `/vacantes/${vacanteId}/cerrar`,
        { motivoCierre, cerradoPor },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al cerrar vacante:', error.response?.data || error.message);
      throw error;
    }
  },

  // ===============================
  // SOLICITUDES
  // ===============================

  async getSolicitudes(usuarioID, rol, token) {
    try {
      const params = new URLSearchParams();
      if (usuarioID) params.append('usuarioID', usuarioID);
      if (rol) params.append('rol', rol);
      const response = await api.get(`/vacantes/solicitudes?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener solicitudes:', error.response?.data || error.message);
      throw error;
    }
  },

  async crearSolicitud(solicitud, token) {
    try {
      const response = await api.post('/vacantes/solicitudes', solicitud, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear solicitud:', error.response?.data || error.message);
      throw error;
    }
  },

  async crearSolicitudVacante(solicitud, token) {
    return this.crearSolicitud(solicitud, token);
  },

  async aprobarSolicitudDirectorArea(solicitudId, aprobadorID, token) {
    try {
      const response = await api.post(
        `/vacantes/solicitudes/${solicitudId}/aprobar-director-area`,
        { aprobadorID },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al aprobar solicitud (Director Área):', error.response?.data || error.message);
      throw error;
    }
  },

  async aprobarSolicitudGerenteRRHH(solicitudId, aprobadorID, token) {
    try {
      const response = await api.post(
        `/vacantes/solicitudes/${solicitudId}/aprobar-gerente-dths`,
        { aprobadorID },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al aprobar solicitud (Gerente RRHH):', error.response?.data || error.message);
      throw error;
    }
  },

  async aprobarSolicitudDirectorRRHH(solicitudId, aprobadorID, token) {
    try {
      const response = await api.post(
        `/vacantes/solicitudes/${solicitudId}/aprobar-director-dths`,
        { aprobadorID },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al aprobar solicitud (Director RRHH):', error.response?.data || error.message);
      throw error;
    }
  },

  async publicarVacanteDesdeSolicitud(solicitudId, publicadorID, token) {
    try {
      const response = await api.post(
        `/vacantes/solicitudes/${solicitudId}/publicar`,
        { publicadorID },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al publicar vacante:', error.response?.data || error.message);
      throw error;
    }
  },

  async rechazarSolicitud(solicitudId, comentarios, token) {
    try {
      const response = await api.post(
        `/vacantes/solicitudes/${solicitudId}/rechazar`,
        { comentarios },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al rechazar solicitud:', error.response?.data || error.message);
      throw error;
    }
  },

  // ===============================
  // POSTULACIONES
  // ===============================

  async getPostulaciones(filtro, token) {
    try {
      const params = new URLSearchParams();
      // Si filtro es 'todas', RRHH ve todo
      if (filtro && filtro !== 'todas') params.append('filtro', filtro);
      const response = await api.get(`/vacantes/postulaciones?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener postulaciones:', error.response?.data || error.message);
      throw error;
    }
  },

  async getPostulacionesEmpleado(empleadoId, token) {
    try {
      const params = new URLSearchParams();
      params.append('empleadoID', empleadoId);
      const response = await api.get(`/vacantes/postulaciones?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener postulaciones del empleado:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Obtiene las postulaciones del equipo del supervisor para validación.
   * El backend filtra por los reportes directos del supervisor.
   */
  async getPostulacionesEquipo(supervisorEmpleadoId, token, rol) {
  try {
    const params = new URLSearchParams();
    if (rol) params.append('rol', rol);  // ← ?rol=coordinador / gerente / director
    const response = await api.get(
      `/vacantes/postulaciones/equipo/${supervisorEmpleadoId}?${params.toString()}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener postulaciones del equipo:', error.response?.data || error.message);
    throw error;
  }
},

  async crearPostulacion(postulacion, token) {
    try {
      const response = await api.post('/vacantes/postulaciones', postulacion, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear postulación:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * El supervisor valida (aprueba/rechaza) la postulación de uno de sus reportes directos.
   * Si aprobado=true → estado pasa a 'Enviado' con timestamp.
   * Si aprobado=false → estado pasa a 'Rechazada por Supervisor'.
   */
  async validarPostulacionSupervisor(postulacionId, aprobado, supervisorEmpleadoId, token, rolSupervisor) {
  try {
    const response = await api.post(
      `/vacantes/postulaciones/${postulacionId}/validar-supervisor`,
      { aprobado, supervisorEmpleadoId, rolSupervisor },  // ← rolSupervisor al body
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return response.data;
  } catch (error) {
    console.error('Error al validar postulación:', error.response?.data || error.message);
    throw error;
  }
},

 async cambiarEstadoPostulacion(postulacionId, estado, comentarios = '', empleadoId = null, token, calificacion = null, evaluacionDetalle = null) {
  try {
    const response = await api.put(
      `/vacantes/postulaciones/${postulacionId}/estado`,
      { estado, comentarios, empleadoId, calificacion, evaluacionDetalle },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado de postulación:', error.response?.data || error.message);
      throw error;
    }
  },

  async getPostulacionesPorVacante(vacanteId, token) {
    try {
      const response = await api.get(`/vacantes/${vacanteId}/postulaciones`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener postulaciones por vacante:', error.response?.data || error.message);
      throw error;
    }
  },

  cambiarEstadoSolicitud: async (id, estado, token) => {
  const response = await api.put(`/vacantes/solicitudes/${id}/estado`, { estado }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
},

  // ===============================
  // ESTADÍSTICAS
  // ===============================

  async getEstadisticas(rol, token) {
    try {
      const params = new URLSearchParams();
      if (rol) params.append('rol', rol);
      const response = await api.get(`/vacantes/estadisticas?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error.response?.data || error.message);
      throw error;
    }
  },

  // ===============================
  // DATOS AUXILIARES
  // ===============================

  async getDepartamentos(token) {
    try {
      const response = await api.get('/vacantes/departamentos', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener departamentos:', error.response?.data || error.message);
      throw error;
    }
  },

  async getUsuarios(token) {
    try {
      const response = await api.get('/vacantes/usuarios', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener usuarios:', error.response?.data || error.message);
      throw error;
    }
  },

  // ===============================
  // HEALTH
  // ===============================

  async testConexion() {
    try {
      const response = await api.get('/vacantes/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async ping() {
    try {
      const response = await api.get('/vacantes/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }
};

export default vacantesService;